// Drug interaction graph. Renders the user's medications as nodes on
// a circle with colored edges for multi-med flags (drug-drug
// interactions, duplicate therapy, anticholinergic burden, etc) and
// glow rings around nodes with single-med flags.
//
// Pure SVG, no D3. Radial layout: nodes are evenly spaced on a
// circle, edges are straight lines colored by the worst severity of
// any flag connecting that pair.

import type { Flag, Medication } from '@medreview/shared';
import { useState } from 'react';

interface InteractionGraphProps {
  medications: Medication[];
  flags: Flag[];
  size?: number;
}

const SEVERITY_COLOR: Record<Flag['severity'], string> = {
  CRITICAL: '#b91c1c',
  MAJOR: '#c2410c',
  MODERATE: '#a16207',
  MINOR: '#475569',
};

const SEVERITY_ORDER: Record<Flag['severity'], number> = {
  CRITICAL: 4,
  MAJOR: 3,
  MODERATE: 2,
  MINOR: 1,
};

function pairKey(a: string, b: string): string {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

export function InteractionGraph({
  medications,
  flags,
  size = 360,
}: InteractionGraphProps): JSX.Element {
  const [hovered, setHovered] = useState<string | null>(null);

  if (medications.length === 0) {
    return <div className="empty-state__body">Add medicines to see your interaction graph.</div>;
  }

  const radius = (size - 96) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const n = medications.length;

  // Node positions on a circle. First node at top, going clockwise.
  const nodes = medications.map((med, i) => {
    const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
    return {
      id: med.id,
      name: med.name,
      x: cx + radius * Math.cos(angle),
      y: cy + radius * Math.sin(angle),
    };
  });
  const byId = new Map(nodes.map((n) => [n.id, n]));

  // Build edge severity map by pair, plus single-med flag map by id.
  const edges = new Map<
    string,
    { a: string; b: string; severity: Flag['severity']; flagCount: number }
  >();
  const haloByMed = new Map<string, { severity: Flag['severity']; count: number }>();

  for (const flag of flags) {
    if (flag.medicationIds.length === 1) {
      const id = flag.medicationIds[0];
      if (!id) continue;
      const prev = haloByMed.get(id);
      if (!prev || SEVERITY_ORDER[flag.severity] > SEVERITY_ORDER[prev.severity]) {
        haloByMed.set(id, { severity: flag.severity, count: (prev?.count ?? 0) + 1 });
      } else {
        haloByMed.set(id, { ...prev, count: prev.count + 1 });
      }
    } else if (flag.medicationIds.length >= 2) {
      for (let i = 0; i < flag.medicationIds.length; i++) {
        for (let j = i + 1; j < flag.medicationIds.length; j++) {
          const a = flag.medicationIds[i];
          const b = flag.medicationIds[j];
          if (!a || !b) continue;
          const key = pairKey(a, b);
          const prev = edges.get(key);
          if (!prev || SEVERITY_ORDER[flag.severity] > SEVERITY_ORDER[prev.severity]) {
            edges.set(key, {
              a,
              b,
              severity: flag.severity,
              flagCount: (prev?.flagCount ?? 0) + 1,
            });
          } else if (prev) {
            edges.set(key, { ...prev, flagCount: prev.flagCount + 1 });
          }
        }
      }
    }
  }

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      width="100%"
      style={{ maxWidth: size, height: 'auto' }}
      role="img"
      aria-label="Drug interaction graph"
    >
      <title>Drug interaction graph</title>
      <defs>
        <filter id="ig-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2.5" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      {/* Background ring guide */}
      <circle
        cx={cx}
        cy={cy}
        r={radius}
        fill="none"
        stroke="#e2d9cc"
        strokeWidth={1}
        strokeDasharray="2,4"
      />

      {/* Edges */}
      {[...edges.values()].map((e) => {
        const a = byId.get(e.a);
        const b = byId.get(e.b);
        if (!a || !b) return null;
        const color = SEVERITY_COLOR[e.severity];
        const opacity =
          hovered && hovered !== a.id && hovered !== b.id
            ? 0.2
            : e.severity === 'CRITICAL'
              ? 0.9
              : 0.75;
        return (
          <line
            key={`${a.id}-${b.id}`}
            x1={a.x}
            y1={a.y}
            x2={b.x}
            y2={b.y}
            stroke={color}
            strokeWidth={e.severity === 'CRITICAL' || e.severity === 'MAJOR' ? 3 : 2}
            opacity={opacity}
            strokeLinecap="round"
          />
        );
      })}

      {/* Nodes */}
      {nodes.map((node) => {
        const halo = haloByMed.get(node.id);
        const isHover = hovered === node.id;
        const haloColor = halo ? SEVERITY_COLOR[halo.severity] : null;
        return (
          <g
            key={node.id}
            onMouseEnter={() => setHovered(node.id)}
            onMouseLeave={() => setHovered(null)}
            style={{ cursor: 'pointer' }}
          >
            {haloColor ? (
              <circle
                cx={node.x}
                cy={node.y}
                r={26}
                fill="none"
                stroke={haloColor}
                strokeWidth={3}
                opacity={isHover ? 1 : 0.7}
                filter="url(#ig-glow)"
              />
            ) : null}
            <circle
              cx={node.x}
              cy={node.y}
              r={20}
              fill={isHover ? '#0f4c75' : '#ffffff'}
              stroke="#0f4c75"
              strokeWidth={2}
            />
            <text
              x={node.x}
              y={node.y + 4}
              textAnchor="middle"
              fontSize="14"
              fontWeight="700"
              fill={isHover ? '#ffffff' : '#0f4c75'}
              style={{ pointerEvents: 'none' }}
            >
              {firstLetters(node.name, 2)}
            </text>
            <text
              x={node.x}
              y={node.y + 36}
              textAnchor="middle"
              fontSize="12"
              fontWeight={isHover ? 700 : 500}
              fill="#1f2937"
              style={{ pointerEvents: 'none' }}
            >
              {trim(node.name, 14)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function firstLetters(name: string, n: number): string {
  const cleaned = name.trim();
  if (!cleaned) return '?';
  const parts = cleaned.split(/\s+/);
  if (parts.length >= n) {
    return parts
      .slice(0, n)
      .map((p) => p[0]?.toUpperCase() ?? '')
      .join('');
  }
  return cleaned.slice(0, n).toUpperCase();
}

function trim(s: string, max: number): string {
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}...`;
}
